import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A modal cart on top of Horizon's native add-to-cart pipeline. All amounts are rendered by Shopify Liquid.
 *
 * Speed design (Oct 2026): a product add used to cost three serial trips to Shopify (the theme's /cart/add,
 * the theme's /cart.json, then our /cart/update.js to add Order Coverage and fetch the drawer HTML).
 * Now the drawer rides along on the theme's own /cart/add request: it asks Shopify to add the coverage
 * line in the same call and to return the drawer HTML with it, so the drawer is complete after ONE trip.
 * The snippet embeds a slim JSON copy of the cart in its HTML, so no extra read is needed to know the lines.
 */
const SECTION = 'viva-harbor-cart-content';

const acStr = (key, fallback) => document.querySelector('viva-harbor-cart-drawer')?.dataset[key] || fallback;

class VivaHarborCartDrawer extends Component {
  #abort; #queue = Promise.resolve(); #busy = false; #optedOut = new Set(); #addonWarnings = new Map();
  #deadline = 0; #timer; #previousFocus; #previousOverflow; #opened = false;
  /** Slim cart embedded in the most recently rendered drawer HTML (item_count, total_price, currency, items[]). */
  #rendered = null;
  /** Counts renders, so a late-arriving theme event cannot overwrite a newer state with older data. */
  #renders = 0;
  get panel() { return this.querySelector('dialog'); }
  get isOpen() { return this.panel?.open === true; }
  get state() { return this.querySelector('.ac-state'); }
  get root() { return window.Shopify?.routes?.root || '/'; }

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#rendered = this.#readCart(this);
    this.#hookAddToCart();
    document.addEventListener('pointerdown', () => this.removeAttribute('data-ac-keyboard-focus'), options);
    document.addEventListener('keydown', event => {
      if (['Tab', 'Enter', ' '].includes(event.key)) this.setAttribute('data-ac-keyboard-focus', '');
    }, options);
    this.addEventListener('click', this.#click, options);
    this.addEventListener('change', this.#change, options);
    this.addEventListener('submit', this.#checkout, options);
    this.panel.addEventListener('cancel', event => { event.preventDefault(); if (this.querySelector('[data-ac-info][aria-expanded="true"]')) this.#hideInfo(); else this.close(); }, options);
    this.addEventListener('pointerover', event => { const button = event.target.closest('[data-ac-info]'); if (event.pointerType === 'mouse' && button) this.#showInfo(button); }, options);
    this.addEventListener('pointerout', event => { const wrap = event.target.closest('.ac-info-wrap'); if (event.pointerType === 'mouse' && wrap && !wrap.contains(event.relatedTarget)) this.#hideInfo(); }, options);
    this.addEventListener('focusin', event => { if (event.target.matches('[data-ac-info]:focus-visible')) this.#showInfo(event.target); }, options);
    this.addEventListener('focusout', event => { const wrap = event.target.closest('.ac-info-wrap'); if (wrap && !wrap.contains(event.relatedTarget)) this.#hideInfo(); }, options);
    this.panel.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = [...this.panel.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),[tabindex="0"]')].filter(e => e.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }, options);
    this.panel.addEventListener('click', event => {
      if (event.target !== this.panel) return;
      const rect = this.panel.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right) this.close();
    }, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#nativeUpdate, options);
    window.addEventListener('pageshow', event => { if (event.persisted && this.isOpen) this.#enqueue(() => this.#refresh()); }, options);
    window.addEventListener('focus', () => { if (this.isOpen) this.#enqueue(() => this.#refresh()); }, options);
    this.#timer = setInterval(() => this.#tick(), 1000);
  }
  disconnectedCallback() {
    super.disconnectedCallback(); this.#abort?.abort(); clearInterval(this.#timer);
    if (this.#opened) document.body.style.overflow = this.#previousOverflow || '';
  }
  toggle() { return this.isOpen ? this.close() : this.open(); }
  open() {
    if (this.isOpen) return this.#queue;
    this.#show();
    return this.#enqueue(() => this.#refresh());
  }
  #show() {
    this.#previousFocus = document.activeElement;
    this.#previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; this.#opened = true;
    this.panel.showModal(); this.setAttribute('open', '');
    this.#announceDrawer(true);
    this.querySelector('[data-ac-close]')?.focus({ preventScroll: true });
  }
  #loading(on) { this.panel.classList.toggle('ac-loading', on); }
  close() {
    if (!this.isOpen) return;
    this.#hideInfo();
    this.panel.close(); this.removeAttribute('open'); this.#opened = false;
    document.body.style.overflow = this.#previousOverflow || '';
    this.#announceDrawer(false);
    if (this.#previousFocus?.isConnected) this.#previousFocus.focus({ preventScroll: true });
  }
  #announceDrawer(open) {
    document.querySelectorAll('[aria-controls="cart-drawer"]').forEach(e => e.setAttribute('aria-expanded', String(open)));
  }

  // ---- Single-trip add: ride along on the theme's /cart/add request -------------------------------------
  /** Wraps window.fetch once so the theme's product-form add carries our add-on line and section request. */
  #hookAddToCart() {
    if (window.__acAddHook) return;
    window.__acAddHook = true;
    const original = window.fetch;
    const drawer = this;
    window.fetch = function (input, init) {
      try {
        const body = init?.body;
        const target = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url;
        const path = target ? new URL(target, window.location.href).pathname : '';
        if (body instanceof FormData && /\/cart\/add(\.js)?$/.test(path) && body.has('id') && !body.has('items[0][id]')) {
          return drawer.#interceptAdd(original, this, input, init);
        }
      } catch { /* fall through to the untouched request */ }
      return original.apply(this, arguments);
    };
  }
  /** Adds the drawer section (and the Order Coverage line when it is due) to the theme's add request, then renders from its response. */
  async #interceptAdd(original, scope, input, init) {
    const body = init.body;
    const url = new URL(window.location.href);
    url.searchParams.delete('sections'); url.searchParams.delete('section_id');
    const sections = String(body.get('sections') || '').split(',').filter(Boolean);
    if (!sections.includes(SECTION)) sections.push(SECTION);
    body.set('sections', sections.join(','));
    if (!body.has('sections_url')) body.set('sections_url', url.pathname + url.search);
    const bundle = this.#addonsDue();
    bundle.forEach((addon, index) => { body.set(`items[${index}][id]`, String(addon.id)); body.set(`items[${index}][quantity]`, '1'); });
    let response = await original.call(scope, input, init);
    if (bundle.length && !response.ok) {
      // Never let an add-on problem block the product: retry exactly what the theme would have sent on its own.
      bundle.forEach((addon, index) => { body.delete(`items[${index}][id]`); body.delete(`items[${index}][quantity]`); });
      response = await original.call(scope, input, init);
    }
    if (response.ok) {
      response.clone().json().then(data => {
        const html = data?.sections?.[SECTION];
        if (html) this.#enqueue(() => this.#refresh(null, { [SECTION]: html }));
      }).catch(() => { /* the theme's own event still refreshes the drawer */ });
    }
    return response;
  }
  /** Add-ons that should be in the cart but are not, according to the last rendered state. */
  #addonsDue() {
    const items = this.#rendered?.items || [];
    return this.#addons().filter(addon => addon.available && !this.#optedOut.has(addon.key) && !items.some(item => item.product_id === addon.product));
  }
  #addons() {
    return [...this.querySelectorAll('[data-ac-addon-config]')].map(e => ({
      key: e.dataset.acAddonConfig, id: Number(e.dataset.variant), product: Number(e.dataset.product),
      available: e.dataset.available === 'true', name: e.dataset.name
    })).filter(a => a.product && a.id);
  }
  /** Slim cart JSON the snippet embeds in the drawer HTML. */
  #readCart(root) {
    const node = root?.querySelector?.('[data-ac-cart]');
    if (!node) return null;
    try { const cart = JSON.parse(node.textContent); return Array.isArray(cart?.items) ? cart : null; } catch { return null; }
  }
  #signature(items) { return (items || []).map(item => `${item.key}:${item.quantity}`).sort().join('|'); }

  #nativeUpdate = async event => {
    if (event.target === this) return;
    // The theme fires this event before it calls Shopify, so open right away and fill in once the add lands.
    const modal = event.target instanceof Element ? event.target.closest('dialog[open]') : null;
    const instant = event.action === 'add' && this.dataset.autoOpen !== 'false' && !this.isOpen && !(modal && modal !== this.panel && modal.matches(':modal'));
    const seen = this.#renders;
    let previous = null;
    if (instant) { this.#show(); previous = this.#preview(event); if (!previous) this.#loading(true); }
    // A failed add must not leave the preview behind: put back what the drawer showed before (unless something real rendered since).
    const unpreview = () => { if (previous && this.#renders === seen) { this.querySelector('[data-ac-content]').replaceChildren(previous); this.panel.setAttribute('aria-busy', 'false'); } };
    try {
      const result = event.promise ? await event.promise : null;
      if (result?.detail?.didError) { if (instant) { this.#loading(false); unpreview(); this.close(); } return; }
      const items = result?.detail?.items || null;
      // The theme resolves its promise only after its own follow-up /cart.json read. If the drawer has rendered since
      // (normally from the add response itself), these items are already stale: do not refresh from them.
      const stale = this.#renders > seen;
      if (event.action === 'add' && this.dataset.autoOpen !== 'false') {
        if (modal && modal !== this.panel && modal.matches(':modal')) {
          modal.addEventListener('close', () => this.open(), { once: true });
        } else if (!this.isOpen) this.open();
        else if (!stale) this.#enqueue(() => this.#refresh(null, null, items));
      } else if (this.isOpen && !stale) this.#enqueue(() => this.#refresh(null, null, items));
      if (stale) this.#loading(false);
    } catch (error) { this.#loading(false); unpreview(); if (this.isOpen) this.#error(error); }
  };
  /**
   * Instant first add: Shopify takes ~0.5-0.8 s to answer, so instead of a spinner the drawer shows the lines, totals and
   * Order Coverage this add will produce, built from the page's bundle tile (data-ac-preview, priced by Liquid) and the
   * drawer's own layout template (data-ac-shell, only present while the cart is empty). Everything stays disabled until the
   * real HTML from the add response replaces it. Returns the state it replaced, or null when no preview is possible.
   */
  #preview(event) {
    try {
      const current = this.querySelector('[data-ac-content] > .ac-state');
      const shell = current?.querySelector(':scope > template[data-ac-shell]');
      const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
      const tile = section?.querySelector('input[data-ac-preview]:checked');
      const product = tile?.closest('[data-ac-product]');
      const quantity = Number(section?.querySelector('form[action*="/cart/add"] input[name="quantity"]')?.value || 0);
      if (!shell || !tile || !product) return null;
      const data = JSON.parse(tile.dataset.acPreview);
      // Only when the tile is what is actually being added (the tiles drive the form's quantity).
      if (quantity !== data.lines.reduce((sum, line) => sum + line.q, 0)) return null;
      const next = current.cloneNode(false);
      next.dataset.count = String(quantity); // the CSS hides the drawer heading while data-count is 0
      next.append(...[...current.children].filter(e => e.matches('script[data-ac-cart], [data-ac-addon-config]')).map(e => e.cloneNode(true)));
      next.append(shell.content.cloneNode(true));
      const sample = next.querySelector('[data-ac-sample]');
      for (const line of data.lines) {
        const row = sample.cloneNode(true); row.removeAttribute('data-ac-sample');
        const image = row.querySelector('[data-p-image]'); image.src = product.dataset.acImage; image.alt = product.dataset.acTitle;
        row.querySelector('[data-p-title]').textContent = product.dataset.acTitle;
        row.querySelector('[data-p-quantity]').value = line.q;
        const regular = row.querySelector('[data-p-regular]'); if (line.save) regular.textContent = line.regular; else regular.remove();
        row.querySelector('[data-p-final]').textContent = line.final;
        if (line.save) row.querySelector('[data-p-saving]').textContent = line.saving; else row.querySelector('[data-p-saving-row]').remove();
        const note = row.querySelector('[data-p-note]'); if (line.note) note.textContent = line.note; else note.remove();
        sample.before(row);
      }
      sample.remove();
      // A first add always ends with Order Coverage in the cart unless the shopper unticked it (or it is unavailable).
      const coverage = this.#addons().some(addon => addon.key === 'coverage' && addon.available && !this.#optedOut.has(addon.key));
      next.querySelector('[data-ac-coverage]')?.setAttribute('aria-checked', String(coverage));
      next.querySelector('[data-p-subtotal]').textContent = coverage ? data.subtotal_coverage : data.subtotal;
      if (data.save) next.querySelector('[data-p-savings]').textContent = data.savings; else next.querySelector('.ac-savings')?.remove();
      next.querySelectorAll('button, input, select').forEach(element => { element.disabled = true; });
      this.querySelector('[data-ac-content]').replaceChildren(next);
      this.panel.setAttribute('aria-busy', 'true');
      if (!this.#deadline) this.#deadline = Date.now() + Number(next.dataset.duration || 300) * 1000;
      this.#tick();
      return current;
    } catch { return null; }
  }
  #enqueue(task) {
    const work = async () => {
      const focusKey = document.activeElement?.dataset?.focus;
      this.#busy = true; this.#setBusy(true); this.#error(null);
      try { await task(); this.#showWarnings(); }
      catch (error) { this.#loading(false); this.#error(error); }
      finally {
        this.#busy = false; this.#setBusy(false);
        if (this.isOpen && focusKey) {
          const focus = this.querySelector(`[data-focus="${CSS.escape(focusKey)}"]`) || this.querySelector('[data-ac-quantity]') || this.querySelector('[data-ac-close]');
          if (focus && !focus.disabled) focus.focus({ preventScroll: true });
        }
      }
    };
    this.#queue = this.#queue.then(work, work); return this.#queue;
  }
  #setBusy(busy) {
    this.panel.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('.ac-content button,.ac-content input,.ac-content select').forEach(element => {
      if (busy) { if (!('wasDisabled' in element.dataset)) element.dataset.wasDisabled = String(element.disabled); element.disabled = true; }
      else if ('wasDisabled' in element.dataset) { element.disabled = element.dataset.wasDisabled === 'true'; delete element.dataset.wasDisabled; }
    });
  }
  #error(error) {
    const region = this.querySelector('[data-ac-error]');
    region.hidden = !error;
    region.textContent = error ? (error.message || acStr('strErrUpdate', 'The cart could not update. Please try again.')) : '';
  }
  /** Add-on warnings outlive individual tasks (each task clears the error region when it starts). */
  #showWarnings() { if (this.#addonWarnings.size) this.#error(new Error([...this.#addonWarnings.values()].join(' '))); }
  async #request(path, payload) {
    if (payload) {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections'); url.searchParams.delete('section_id');
      payload = { ...payload, sections: [SECTION], sections_url: url.pathname + url.search };
    }
    const response = await fetch(this.root + path, {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {}), signal: AbortSignal.timeout(15000)
    });
    let data; try { data = await response.json(); } catch { throw new Error(acStr('strErrUpdate', 'The cart could not update. Please try again.')); }
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || acStr('strErrUpdate', 'The cart could not update. Please try again.'));
    return data;
  }
  /** Which add-on lines need adding/removing/normalising for this set of cart lines. */
  #plan(items) {
    const addons = this.#addons();
    const addonIds = new Set(addons.map(a => a.product));
    const goods = items.filter(item => !addonIds.has(item.product_id));
    const updates = {}; const adding = [];
    if (!goods.length) {
      items.filter(item => addonIds.has(item.product_id)).forEach(item => { updates[item.key] = 0; });
      this.#optedOut.clear(); this.#addonWarnings.clear(); this.#deadline = 0;
    } else {
      for (const addon of addons) {
        const lines = items.filter(item => item.product_id === addon.product);
        if (!lines.length && addon.available && !this.#optedOut.has(addon.key)) { updates[addon.id] = 1; adding.push(addon); }
        else if (lines.length && (this.#optedOut.has(addon.key) || lines.length > 1 || lines[0].quantity !== 1)) {
          lines.forEach((item, index) => { updates[item.key] = this.#optedOut.has(addon.key) || index > 0 ? 0 : 1; });
        }
      }
    }
    return { updates, adding, goods };
  }
  async #refresh(cart = null, sections = null, items = null) {
    // cart/update.js returns the cart AND the drawer HTML, so each refresh is a single trip to Shopify.
    const sync = updates => this.#request('cart/update.js', { updates });
    // Drawer HTML carries a slim copy of the cart, so HTML alone is enough to know the lines.
    if (!cart && !items && sections?.[SECTION]) cart = this.#readCart(new DOMParser().parseFromString(sections[SECTION], 'text/html'));
    // With no fresh data, plan from the lines currently shown (a wrong guess is caught below and reconciled).
    if (!cart && !items && this.#rendered) items = this.#rendered.items;
    if (!cart && !items) cart = await sync({});
    items = cart?.items || items;
    if (cart) sections ||= cart.sections;
    const config = this.state?.dataset || {};
    const { updates, adding, goods } = this.#plan(items);
    if (goods.length && !this.#deadline) this.#deadline = Date.now() + Number(config.duration || 300) * 1000;
    // Nothing to change and the drawer already shows exactly these lines (e.g. the theme's follow-up event after a single-trip add): done.
    if (!Object.keys(updates).length && !sections && this.#rendered && items !== this.#rendered.items && this.#signature(items) === this.#signature(this.#rendered.items)) { this.#loading(false); return; }
    if (Object.keys(updates).length) {
      try { cart = await sync(updates); adding.forEach(addon => this.#addonWarnings.delete(addon.key)); }
      catch (error) {
        // Reconcile uncertain responses before deciding whether a charge exists.
        cart = await sync({});
        for (const addon of adding) {
          if (!cart.items.some(item => item.product_id === addon.product)) {
            this.#optedOut.add(addon.key);
            this.#addonWarnings.set(addon.key, acStr('strAddonFail', '[name] could not be added. You can check out without it, or check its box to retry.').replace('[name]', addon.name));
          }
        }
      }
      sections = cart.sections;
    } else if (!sections) { cart = await sync({}); sections = cart.sections; }
    await this.#render(sections?.[SECTION]);
    this.#publish(cart || this.#rendered);
  }
  async #render(html = null) {
    const body = this.querySelector('.ac-body'); const scroll = body?.scrollTop || 0;
    const selections = [...this.querySelectorAll('[data-ac-variant]')].map(select => [select.dataset.focus, select.value]);
    if (!html) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id'); url.searchParams.set('sections', SECTION);
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('The cart updated, but could not refresh. Please reopen it.');
      const sections = await response.json();
      html = sections[SECTION];
    }
    if (!html) throw new Error('The cart updated, but could not refresh. Please reopen it.');
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.ac-state');
    if (!parsed) throw new Error(acStr('strErrDisplay', 'The cart could not be displayed. Please reopen it.'));
    this.querySelector('[data-ac-content]').replaceChildren(parsed);
    this.#rendered = this.#readCart(parsed) || this.#rendered;
    this.#renders++;
    this.#loading(false);
    for (const [key, value] of selections) {
      const select = this.querySelector(`[data-focus="${CSS.escape(key)}"]`);
      if (select?.querySelector(`option[value="${CSS.escape(value)}"]:not(:disabled)`)) { select.value = value; this.#selectVariant(select); }
    }
    if (this.#busy) this.#setBusy(true);
    const nextBody = this.querySelector('.ac-body'); if (nextBody) nextBody.scrollTop = scroll;
    this.#tick();
  }
  #publish(cart) {
    if (!cart?.items) return;
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { source: 'viva-harbor-cart', itemCount: cart.item_count, didError: false, items: cart.items } }) }));
    this.querySelector('[data-ac-status]').textContent = acStr('strUpdated', 'Cart updated. [count] items.').replace('[count]', cart.item_count);
  }
  #tick() {
    const timer = this.querySelector('[data-ac-countdown]'); if (!timer || !this.#deadline) return;
    // The reference restarts at 01:30 on expiry and leaves the cart intact.
    if (this.#deadline <= Date.now()) this.#deadline = Date.now() + 90000;
    const seconds = Math.ceil((this.#deadline - Date.now()) / 1000);
    timer.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }
  #hideInfo() {
    this.querySelectorAll('[data-ac-info]').forEach(button => { button.setAttribute('aria-expanded', 'false'); this.querySelector(`#${button.getAttribute('aria-controls')}`)?.setAttribute('hidden', ''); });
  }
  #showInfo(button) {
    this.#hideInfo(); button.setAttribute('aria-expanded', 'true');
    const tip = this.querySelector(`#${button.getAttribute('aria-controls')}`);
    if (tip) {
      tip.removeAttribute('hidden');
      const anchor = button.getBoundingClientRect(), bubble = tip.getBoundingClientRect();
      tip.style.setProperty('--ac-tip-arrow', `${Math.max(12, Math.min(bubble.width - 24, anchor.left + anchor.width / 2 - bubble.left - 6))}px`);
    }
  }
  #click = event => {
    const info = event.target.closest('[data-ac-info]');
    if (info) { if (info.getAttribute('aria-expanded') === 'true') this.#hideInfo(); else this.#showInfo(info); return; }
    if (!event.target.closest('.ac-info-wrap')) this.#hideInfo();
    const button = event.target.closest('button'); if (!button) return;
    if (button.hasAttribute('data-ac-close')) { this.close(); return; }
    if (this.#busy || button.disabled) return;
    if (button.hasAttribute('data-ac-add')) {
      this.#enqueue(async () => { const result = await this.#request('cart/add.js', { items: [{ id: Number(button.dataset.acAdd), quantity: 1 }] }); await this.#refresh(null, result.sections); });
    } else if (button.hasAttribute('data-ac-addon')) {
      const key = button.dataset.acAddon, next = button.getAttribute('aria-checked') === 'true';
      this.#enqueue(async () => {
        const previous = this.#optedOut.has(key);
        if (next) this.#optedOut.add(key); else this.#optedOut.delete(key);
        this.#addonWarnings.delete(key);
        try { await this.#refresh(); } catch (error) { if (previous) this.#optedOut.add(key); else this.#optedOut.delete(key); throw error; }
      });
    } else if (button.hasAttribute('data-ac-remove') || button.hasAttribute('data-ac-step')) {
      const line = button.closest('[data-key]'), input = line.querySelector('[data-ac-quantity]');
      const quantity = button.hasAttribute('data-ac-remove') ? 0 : Math.max(Number(input.min) || 1, Number(input.value) + Number(button.dataset.acStep) * (Number(input.step) || 1));
      this.#changeLine(line.dataset.key, quantity);
    }
  };
  #selectVariant(select) {
    const card = select.closest('[data-upsell]'), option = select.selectedOptions[0];
    if (!option) return;
    card.querySelector('[data-ac-add]').dataset.acAdd = option.value;
    card.querySelector('[data-ac-upsell-price]').textContent = option.dataset.price;
    const compare = card.querySelector('[data-ac-upsell-compare]');
    compare.textContent = option.dataset.compare || ''; compare.hidden = !option.dataset.compare;
  }
  #change = event => {
    const select = event.target.closest('[data-ac-variant]');
    if (select && !this.#busy) { this.#selectVariant(select); return; }
    const input = event.target.closest('[data-ac-quantity]'); if (!input || this.#busy) return;
    const quantity = Number(input.value), min = Number(input.min) || 1, step = Number(input.step) || 1;
    if (!Number.isSafeInteger(quantity) || quantity < 0 || (quantity !== 0 && (quantity < min || (quantity - min) % step))) {
      this.#error(new Error(acStr('strErrQty', 'Please enter a valid quantity.'))); return;
    }
    this.#changeLine(input.closest('[data-key]').dataset.key, quantity);
  };
  #changeLine(key, quantity) {
    this.#enqueue(async () => {
      const items = this.#rendered?.items;
      if (items?.some(item => item.key === key)) {
        // One trip: the line change plus whatever the add-on rules require after it (e.g. dropping Order Coverage with the last product).
        const next = items.map(item => item.key === key ? { ...item, quantity } : item).filter(item => item.quantity > 0);
        const { updates } = this.#plan(next);
        const cart = await this.#request('cart/update.js', { updates: { ...updates, [key]: quantity } });
        await this.#refresh(cart);
      } else {
        const cart = await this.#request('cart/change.js', { id: key, quantity }); await this.#refresh(cart);
      }
    });
  }
  #checkout = event => {
    if (!event.target.matches('.ac-checkout-form')) return;
    event.preventDefault(); if (this.#busy) return;
    this.#enqueue(async () => {
      // Only round-trip to Shopify when the add-on lines shown do not match the shopper's choices.
      if (!this.#rendered || Object.keys(this.#plan(this.#rendered.items).updates).length) await this.#refresh();
      const form = this.querySelector('.ac-checkout-form'); if (!form) return;
      const input = document.createElement('input'); input.type = 'hidden'; input.name = 'checkout'; input.value = 'Check out'; form.append(input);
      HTMLFormElement.prototype.submit.call(form);
    });
  };
}
if (!customElements.get('viva-harbor-cart-drawer')) customElements.define('viva-harbor-cart-drawer', VivaHarborCartDrawer);
